import { _js } from '@ifixit/utils/localize';
import { Checkbox } from '@ifixit/ui/components/checkbox';
import { cn } from '@ifixit/ui/lib/utils';
import { useId, useRef, useState } from 'react';
import { DualRangeSlider, RangeSlider } from 'Shared/editor/RangeSlider';
import { thumbCenterOffset } from 'Shared/editor/range_slider_style';
import type { TimeIncrement } from './types';

const MINUTE = 60;
const HOUR = 3600;
const WEEK = 604800;

/**
 * Landmark increments labeled under the track, picked for even spacing on
 * the slider's index scale, not the time scale: the increments compress the
 * day steps into the last tenth of the track, so only the week endpoint
 * gets a label there. Mobile keeps three (the narrower set that can't
 * collide at ~360px); the rest join at md.
 */
const TICKS: Array<{ seconds: number; onMobile?: boolean }> = [
   { seconds: 0 },
   { seconds: 5 * MINUTE, onMobile: true },
   { seconds: HOUR, onMobile: true },
   { seconds: 12 * HOUR },
   { seconds: WEEK, onMobile: true },
];

/**
 * The time estimate control: a dual-thumb slider over the discrete
 * increments GuideIntroLib::getTimeIncrements defines, with a Range toggle
 * that collapses it to a single thumb (min == max). Values snap to the
 * increments; the readout combines them ("20-45 minutes").
 */
export function TimeSlider({
   increments,
   minSeconds,
   maxSeconds,
   isRange,
   onChange,
}: {
   increments: TimeIncrement[];
   minSeconds: number | null;
   maxSeconds: number | null;
   isRange: boolean;
   onChange: (minSeconds: number | null, maxSeconds: number | null, isRange: boolean) => void;
}) {
   const toggleId = useId();
   const lastIndex = increments.length - 1;
   const propMinIndex = nearestIndex(increments, minSeconds);
   const propMaxIndex = nearestIndex(increments, maxSeconds);

   // A drag tick fires onValueChange on every pointermove; propagating each
   // one to the parent (and from there back down as props) re-renders the
   // whole Details tab per pixel of travel. Buffer locally while a thumb is
   // down and commit once on release, so the parent only sees the final
   // value. Null while idle, so the props stay the source of truth between
   // drags (including for an external change, like loading a new guide).
   const [draftIndices, setDraftIndices] = useState<{ min: number; max: number } | null>(null);
   // The slider values are indices into `increments`; without this a screen
   // reader announces the raw index ("3") instead of the duration it stands
   // for ("30 minutes").
   const formatIncrement = (index: number) => increments[index]?.label ?? '';
   // Mirrors draftIndices for the release handler: the thumb's flush and the
   // wrapper's pointerup run in the same event dispatch, so the state closure
   // endDrag sees is one update behind the value the flush just buffered.
   const draftRef = useRef<{ min: number; max: number } | null>(null);
   const isDraggingRef = useRef(false);
   const minIndex = draftIndices ? draftIndices.min : propMinIndex;
   const maxIndex = draftIndices ? draftIndices.max : propMaxIndex;

   // isRange is a plain prop owned by the page's fields, so dragging the two
   // thumbs together — which momentarily makes min == max — can't unmount the
   // dual slider mid-drag the way a value-derived flag would. The toggle and
   // the thumbs report through one onChange so the page applies the mode and
   // the values in a single update.
   const emit = (nextMinIndex: number, nextMaxIndex: number, nextIsRange: boolean) => {
      // Ordering the thumbs here also lets dragging the low thumb to 0 mean
      // "no lower bound", collapsing the readout to "under X".
      const low = Math.min(nextMinIndex, nextMaxIndex);
      const high = Math.max(nextMinIndex, nextMaxIndex);
      draftRef.current = null;
      setDraftIndices(null);
      onChange(secondsAt(increments, low), secondsAt(increments, high), nextIsRange);
   };

   // Dragging a thumb keeps the current range mode. A pointer drag buffers
   // the value locally (the readout still updates every tick) and only
   // commits to the parent on release; a keyboard nudge has no separate
   // "release" event, so it commits immediately, same as before.
   const commit = (nextMinIndex: number, nextMaxIndex: number) => {
      if (isDraggingRef.current) {
         draftRef.current = { min: nextMinIndex, max: nextMaxIndex };
         setDraftIndices(draftRef.current);
         return;
      }
      emit(nextMinIndex, nextMaxIndex, isRange);
   };

   // Pointer events on the thumbs bubble up through this wrapper (the native
   // range inputs aren't stopPropagation'd), so one pair of handlers covers
   // both thumbs of the dual slider without threading drag state through
   // RangeSlider/DualRangeSlider.
   const beginDrag = () => {
      isDraggingRef.current = true;
   };
   const endDrag = () => {
      if (!isDraggingRef.current) {
         return;
      }
      isDraggingRef.current = false;
      const draft = draftRef.current;
      if (draft) {
         emit(draft.min, draft.max, isRange);
      }
   };

   const setRange = (enabled: boolean) => {
      if (!enabled) {
         // Collapse to a single value: pin the low thumb to the high one.
         emit(maxIndex, maxIndex, false);
      } else if (minIndex === maxIndex) {
         // Spread the thumbs so enabling always visibly arms the range: drop
         // the low thumb one increment, or (at "no estimate", with no room
         // below) raise the high thumb instead.
         if (minIndex > 0) {
            emit(minIndex - 1, maxIndex, true);
         } else {
            emit(0, Math.min(lastIndex, maxIndex + 1), true);
         }
      } else {
         // Already spans two increments — just flip the mode on.
         emit(minIndex, maxIndex, true);
      }
   };

   return (
      <div>
         {/* The readout sits beside its label so they read as one unit. The
             label hugs the left and the Range checkbox the right (ml-auto), so
             the readout growing ("30 minutes" → "1 hour - 2 days") never
             reflows either neighbor. */}
         <div className="mb-1 flex items-center gap-x-2">
            <span className="text-sm font-medium text-gray-950">{_js('Time required')}</span>
            {/* The readout IS the control's value, not a hint: it reads in the
                darker value color while the label stays quiet. */}
            <span id={toggleId} className="text-sm text-gray-900">
               {readout(increments, minIndex, maxIndex)}
            </span>
            <label className="ml-auto flex items-center gap-1.5 text-sm text-gray-600">
               <Checkbox
                  checked={isRange}
                  onCheckedChange={checked => setRange(checked === true)}
               />
               {_js('Range')}
            </label>
         </div>
         {/* The thumbs are native range inputs; their pointer events bubble
             here uncaught, so this one wrapper catches both a drag's start
             and end (release, or the pointer leaving without a clean
             pointerup) without threading drag state through the slider
             components themselves. */}
         <div
            className="py-2"
            onPointerDown={beginDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onBlur={endDrag}
         >
            {isRange ? (
               <DualRangeSlider
                  max={lastIndex}
                  low={minIndex}
                  high={maxIndex}
                  lowLabel={_js('Minimum time')}
                  highLabel={_js('Maximum time')}
                  describedBy={toggleId}
                  formatValue={formatIncrement}
                  onValueChange={commit}
               />
            ) : (
               <RangeSlider
                  max={lastIndex}
                  value={maxIndex}
                  ariaLabel={_js('Maximum time')}
                  describedBy={toggleId}
                  formatValue={formatIncrement}
                  onValueChange={value => commit(value, value)}
               />
            )}
         </div>
         <TickLabels increments={increments} />
      </div>
   );
}

/**
 * The landmark labels under the track, anchored to the same thumb-center
 * travel as the fill (fillStyle's math), so each label sits under the spot
 * the thumb actually reaches for that increment. aria-hidden: the thumbs
 * already announce their value through aria-valuetext.
 */
function TickLabels({ increments }: { increments: TimeIncrement[] }) {
   const lastIndex = increments.length - 1;
   const ticks = TICKS.flatMap(tick => {
      const index = increments.findIndex(increment => increment.seconds === tick.seconds);
      return index === -1 ? [] : [{ ...tick, index, label: increments[index].label }];
   });
   if (ticks.length === 0) {
      return null;
   }
   return (
      <div aria-hidden className="relative h-4 text-xs text-gray-500">
         {ticks.map(tick => (
            <span
               key={tick.seconds}
               className={cn(
                  'absolute whitespace-nowrap',
                  !tick.onMobile && 'max-md:hidden',
                  // The endpoint labels hug their track edge instead of
                  // centering, which would overflow the container.
                  tick.index !== 0 && tick.index !== lastIndex && '-translate-x-1/2'
               )}
               style={tickPosition(tick.index, lastIndex)}
            >
               {tick.label}
            </span>
         ))}
      </div>
   );
}

function tickPosition(index: number, lastIndex: number): { left?: string; right?: string } {
   if (index === 0) {
      return { left: '0' };
   }
   if (index === lastIndex) {
      return { right: '0' };
   }
   const fraction = lastIndex <= 0 ? 0 : index / lastIndex;
   return { left: thumbCenterOffset(fraction) };
}

function nearestIndex(increments: TimeIncrement[], seconds: number | null): number {
   if (seconds === null || increments.length === 0) {
      return 0;
   }
   let best = 0;
   for (let index = 1; index < increments.length; index++) {
      if (
         Math.abs(increments[index].seconds - seconds) <
         Math.abs(increments[best].seconds - seconds)
      ) {
         best = index;
      }
   }
   return best;
}

function secondsAt(increments: TimeIncrement[], index: number): number | null {
   const seconds = increments[index]?.seconds ?? 0;
   return seconds === 0 ? null : seconds;
}

/** The combined readout: "30 minutes", "20 minutes - 45 minutes". The labels
 *  arrive localized from the server, so the range is composed with a
 *  translatable template instead of splicing words out of the labels. */
function readout(increments: TimeIncrement[], minIndex: number, maxIndex: number): string {
   const from = increments[Math.min(minIndex, maxIndex)]?.label ?? '';
   const to = increments[Math.max(minIndex, maxIndex)]?.label ?? '';
   if (from === to) {
      return from;
   }
   if (Math.min(minIndex, maxIndex) === 0) {
      return to;
   }
   return _js('%1 - %2', from, to);
}
